/**
 * Smart-merge a default search (page-level scope, e.g. tenantId + year) with
 * an active omnisearch string. Mirrors the same merge done inside
 * `OmnisearchDataDisplay`: a key in `omnisearch` always wins over the same
 * key in `defaultSearch`, preventing duplicate filters from arriving at the
 * backend as `tenantId:abc tenantId:abc`.
 *
 * This is exposed as a module-level helper (not a hook) so that consumers
 * like `ManifestShowFilterPicker` can reuse it without tripping the
 * `react-refresh/only-export-components` lint rule on a component file.
 */
/**
 * Omnisearch keys that scope the manifests grid by a correction/raised date —
 * the vendor dashboard's chart drilldowns. When one is present, the page-level
 * `shippedDate:<year>` default is dropped from the hidden default search: the
 * charts scope by these dates (not shipped date), so keeping the shipped-year
 * bound would silently exclude cross-year manifests the chart counted (e.g.
 * shipped Dec 2025, correction requested Jan 2026).
 */
const CORRECTION_DATE_KEYS = [
  "latestEpaCorrectionRequestedAt",
  "flaggedRaisedAt",
  "closureRaisedAt",
] as const;

export function omnisearchHasCorrectionDateTerm(omnisearch: string): boolean {
  // Anchor at a term boundary (start or whitespace) so a key name inside a
  // quoted value doesn't count.
  return CORRECTION_DATE_KEYS.some((key) => new RegExp(`(?:^|\\s)${key}:`).test(omnisearch));
}

export function mergeDefaultAndOmnisearch(defaultSearch: string, omnisearch: string): string {
  const def = defaultSearch.trim();
  const om = omnisearch.trim();
  if (!om) return def;
  if (!def) return om;

  const omKeys = new Set<string>();
  const keyRegex = /([^:\s]+):/g;
  let match: RegExpExecArray | null;
  while ((match = keyRegex.exec(om)) !== null) {
    omKeys.add(match[1]);
  }

  const tokens = def.split(/\s+/).filter(Boolean);
  const filtered = tokens.filter((token) => {
    const colonIdx = token.indexOf(":");
    if (colonIdx <= 0) return true;
    const key = token.substring(0, colonIdx);
    return !omKeys.has(key);
  });
  return [filtered.join(" "), om].filter(Boolean).join(" ").trim();
}
